import { useEffect, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import { useListCustomerAddresses } from '../../features/customers/api';

import { useReplicateLoads, type ReplicateLoadSpec } from '../../features/org-orders/api';

import { apiErrorMessage } from '../../utils/errors';

import { SearchableSelect } from '../SearchableSelect/SearchableSelect';

import './ReplicateOrderDialog.scss';

interface ReplicateDefaults {
  productId: string;

  deliveryAddressId: string;

  orgLoadOptionId: string;

  quantity: number;
}

interface ReplicateOrderDialogProps {
  isOpen: boolean;

  onClose: () => void;

  orderId: string;

  sourceItemId: string;

  /** Display only, e.g. "D0349". */

  sourceLoadNumber: string;

  customerId: string;

  customerName: string;

  hasCarrier: boolean;

  defaults: ReplicateDefaults;

  onReplicated?: (count: number) => void;

  /** Called when exactly one load was created, to open its details modal. */
  onReplicatedItem?: (newItemId: string) => void;
}

interface RowState {
  key: string;

  productId: string;

  deliveryAddressId: string;

  orgLoadOptionId: string;

  quantity: string;

  poNumber: string;
}

interface ReplicateLoadOption {
  id: string;
  name: string;
  capacityLabel?: string | null;
}

const MAX_COPIES = 50;

let rowKeySeq = 0;

const nextKey = () => `row-${++rowKeySeq}`;

export function ReplicateOrderDialog({
  isOpen,

  onClose,

  orderId,

  sourceItemId,

  sourceLoadNumber,

  customerId,

  customerName,

  hasCarrier,

  defaults,

  onReplicated,

  onReplicatedItem,
}: ReplicateOrderDialogProps) {
  const { data, isLoading } = useListCustomerAddresses(isOpen ? customerId : null);

  const replicate = useReplicateLoads(orderId);

  const addresses = useMemo(() => data?.addresses ?? [], [data]);

  const products = useMemo(() => data?.products ?? [], [data]);

  const productOptions = useMemo(
    () => products.map((p) => ({ id: p.id, name: p.name })),

    [products]
  );

  const allLoadOptions = useMemo(() => data?.loadOptions ?? [], [data]);

  const [rows, setRows] = useState<RowState[]>([]);
  const [copyCarrier, setCopyCarrier] = useState(false);

  // Methods available for a given sub-location (its assigned methods; falls
  // back to every org method if the location has none configured).
  //
  // Keep capacityLabel here because it is now the source of the row's default
  // quantity.
  const methodsForAddress = (deliveryAddressId: string): ReplicateLoadOption[] => {
    const address = addresses.find((a) => a.id === deliveryAddressId);
    const scoped = address?.loadOptions ?? [];
    const source = scoped.length > 0 ? scoped : allLoadOptions;

    return source.map((o) => ({
      id: o.id,
      name: o.name,
      capacityLabel: o.capacityLabel ?? null,
    }));
  };

  const quantityForMethod = (deliveryAddressId: string, orgLoadOptionId: string): string => {
    if (!deliveryAddressId || !orgLoadOptionId) {
      return '';
    }

    const method = methodsForAddress(deliveryAddressId).find(
      (option) => option.id === orgLoadOptionId
    );

    const capacity = Number(method?.capacityLabel);

    return Number.isFinite(capacity) && capacity > 0 ? String(capacity) : '';
  };

  const makeRow = (): RowState => {
    const methods = methodsForAddress(defaults.deliveryAddressId);

    const defaultMethodId = methods.some((method) => method.id === defaults.orgLoadOptionId)
      ? defaults.orgLoadOptionId
      : (methods[0]?.id ?? '');

    return {
      key: nextKey(),
      productId: defaults.productId,
      deliveryAddressId: defaults.deliveryAddressId,
      orgLoadOptionId: defaultMethodId,

      // Do NOT copy defaults.quantity from the source order.
      quantity: quantityForMethod(defaults.deliveryAddressId, defaultMethodId),

      poNumber: '',
    };
  };

  // Seed a single row once the address / fulfilment-method data is ready.
  // Waiting for isLoading to finish is important because capacityLabel comes
  // from useListCustomerAddresses().
  useEffect(() => {
    if (!isOpen || isLoading) return;

    setRows([makeRow()]);
    setCopyCarrier(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isLoading, defaults.productId, defaults.deliveryAddressId, defaults.orgLoadOptionId]);

  const addressRequiresPo = (deliveryAddressId: string) =>
    addresses.find((a) => a.id === deliveryAddressId)?.requiresPoNumber === true;

  const patchRow = (key: string, patch: Partial<RowState>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;

        const next = { ...r, ...patch };

        const addressChanged =
          patch.deliveryAddressId !== undefined && patch.deliveryAddressId !== r.deliveryAddressId;

        const methodChanged =
          patch.orgLoadOptionId !== undefined && patch.orgLoadOptionId !== r.orgLoadOptionId;

        // Keep the fulfilment method valid when the sub-location changes.
        if (addressChanged) {
          const methods = methodsForAddress(next.deliveryAddressId);

          if (!methods.some((method) => method.id === next.orgLoadOptionId)) {
            next.orgLoadOptionId = methods[0]?.id ?? '';
          }
        }

        /*
         * Whenever either the address or fulfilment method changes, reset Qty
         * to the selected method's capacityLabel.
         *
         * Quantity remains editable afterwards; this only controls its default.
         */
        if (addressChanged || methodChanged) {
          next.quantity = quantityForMethod(next.deliveryAddressId, next.orgLoadOptionId);
        }

        return next;
      })
    );
  };

  const duplicateRow = (source: RowState): RowState => ({
    ...source,
    key: nextKey(),

    // A newly-created copy always starts from the fulfilment method's capacity,
    // even if the previous row's quantity was manually changed.
    quantity: quantityForMethod(source.deliveryAddressId, source.orgLoadOptionId),

    poNumber: '',
  });

  const setCopies = (count: number) => {
    const target = Math.max(1, Math.min(MAX_COPIES, count));

    setRows((prev) => {
      if (target === prev.length) return prev;

      if (target < prev.length) {
        return prev.slice(0, target);
      }

      const source = prev[prev.length - 1] ?? makeRow();

      const additions = Array.from({ length: target - prev.length }, () => duplicateRow(source));

      return [...prev, ...additions];
    });
  };

  const addCopy = () =>
    setRows((prev) => {
      const source = prev[prev.length - 1] ?? makeRow();

      return [...prev, duplicateRow(source)];
    });

  const removeRow = (key: string) =>
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));

  const rowIsValid = (r: RowState) => {
    const qty = Number(r.quantity);

    if (!r.productId || !r.deliveryAddressId || !r.orgLoadOptionId) return false;

    if (!Number.isFinite(qty) || qty <= 0) return false;

    if (addressRequiresPo(r.deliveryAddressId) && !r.poNumber.trim()) return false;

    return true;
  };

  const canCreate = rows.length > 0 && rows.every(rowIsValid) && !replicate.isPending && !isLoading;

  const handleCreate = async () => {
    if (!canCreate) return;

    const loads: ReplicateLoadSpec[] = rows.map((r) => ({
      productId: r.productId,

      deliveryAddressId: r.deliveryAddressId,

      orgLoadOptionId: r.orgLoadOptionId,

      quantity: Number(r.quantity),

      poNumber: r.poNumber.trim() || null,
    }));

    try {
      const res = await replicate.mutateAsync({
        sourceItemId,

        copyCarrier: copyCarrier && hasCarrier,

        loads,
      });

      toast.success(
        `${res.createdCount} new load${res.createdCount === 1 ? '' : 's'} created from ${sourceLoadNumber}.`
      );

      onClose();

      // A single replicated load opens its own details modal; multiples fall
      // back to the refreshed orders table (existing behavior).
      const newItemId = res.itemIds?.[0];
      if (res.createdCount === 1 && newItemId && onReplicatedItem) {
        onReplicatedItem(newItemId);
      } else {
        onReplicated?.(res.createdCount);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to replicate loads.'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal replicate-modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={replicate.isPending ? undefined : onClose} />

      <div className="modal__panel replicate-modal__panel">
        <div className="modal__header">
          <div>
            <h3 className="modal__title">Replicate Order</h3>

            <div className="muted tiny" style={{ marginTop: 4 }}>
              Copying <strong>{sourceLoadNumber}</strong> · {customerName}. Each row below becomes a
              new load.
            </div>
          </div>

          <button className="btn btn--ghost" onClick={onClose} disabled={replicate.isPending}>
            Close
          </button>
        </div>

        <div className="modal__body">
          <div className="replicate-modal__controls">
            <div className="replicate-modal__copies">
              <span className="replicate-modal__copies-label">Copies</span>

              <div className="replicate-modal__stepper">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setCopies(rows.length - 1)}
                  disabled={rows.length <= 1}
                  aria-label="One fewer copy"
                >
                  −
                </button>

                <input
                  type="number"
                  min={1}
                  max={MAX_COPIES}
                  value={rows.length}
                  onChange={(e) => setCopies(Number(e.target.value) || 1)}
                  className="replicate-modal__copies-input"
                />

                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setCopies(rows.length + 1)}
                  disabled={rows.length >= MAX_COPIES}
                  aria-label="One more copy"
                >
                  +
                </button>
              </div>
            </div>

            <span className="muted tiny">Load numbers are assigned on save.</span>
          </div>

          {isLoading ? (
            <div className="skeleton" style={{ height: 180 }} />
          ) : (
            <div className="replicate-modal__table-wrap">
              <table className="replicate-modal__table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }} />

                    <th>Load Number</th>

                    <th>Product</th>

                    <th style={{ width: 96 }}>Qty (t)</th>

                    <th style={{ width: 130 }}>PO</th>

                    <th>Sub Location</th>

                    <th>Fulfilment Method</th>

                    <th style={{ width: 36 }} />
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r, index) => {
                    const requiresPo = addressRequiresPo(r.deliveryAddressId);

                    const poMissing = requiresPo && !r.poNumber.trim();

                    const methods = methodsForAddress(r.deliveryAddressId);

                    return (
                      <tr key={r.key}>
                        <td className="muted">{index + 1}</td>

                        <td>
                          <span className="replicate-modal__auto">Auto</span>
                        </td>

                        <td>
                          <SearchableSelect
                            value={r.productId}
                            options={productOptions}
                            onChange={(id) => patchRow(r.key, { productId: id })}
                            placeholder="Search products…"
                            ariaLabel="Product"
                          />
                        </td>

                        <td>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={r.quantity}
                            onChange={(e) => patchRow(r.key, { quantity: e.target.value })}
                          />
                        </td>

                        <td>
                          <input
                            type="text"
                            value={r.poNumber}
                            placeholder={requiresPo ? 'Required' : '—'}
                            className={poMissing ? 'replicate-modal__po--required' : ''}
                            onChange={(e) => patchRow(r.key, { poNumber: e.target.value })}
                          />

                          {poMissing && <div className="replicate-modal__po-hint">PO required</div>}
                        </td>

                        <td>
                          <select
                            value={r.deliveryAddressId}
                            onChange={(e) => patchRow(r.key, { deliveryAddressId: e.target.value })}
                          >
                            {addresses.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.label || a.line1 || 'Sub-location'}

                                {a.requiresPoNumber ? ' • PO' : ''}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <select
                            value={r.orgLoadOptionId}
                            onChange={(e) => patchRow(r.key, { orgLoadOptionId: e.target.value })}
                          >
                            {methods.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost replicate-modal__remove"
                            onClick={() => removeRow(r.key)}
                            disabled={rows.length <= 1}
                            aria-label={`Remove copy ${index + 1}`}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <button
                type="button"
                className="btn btn--ghost replicate-modal__add"
                onClick={addCopy}
                disabled={rows.length >= MAX_COPIES}
              >
                + Add another copy
              </button>
            </div>
          )}

          {hasCarrier && (
            <label className="replicate-modal__carrier">
              <input
                type="checkbox"
                checked={copyCarrier}
                onChange={(e) => setCopyCarrier(e.target.checked)}
              />
              Copy assigned carrier (new loads start in Assigned status)
            </label>
          )}
        </div>

        <div className="modal__footer replicate-modal__footer">
          <span className="muted tiny">
            {rows.length} new load{rows.length === 1 ? '' : 's'} will be created from{' '}
            {sourceLoadNumber}.
          </span>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--ghost" onClick={onClose} disabled={replicate.isPending}>
              Cancel
            </button>

            <button className="btn btn--primary" onClick={handleCreate} disabled={!canCreate}>
              {replicate.isPending ? 'Creating…' : 'Create Loads'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
